import {lazy} from 'react';
import MessageType from '../../../enums/MessageType';
import {getDisplayableCards} from '../../../v2/selectors/DisplayableCardsSelectors/DisplayableCardsSelectors';
import isArabicLanguageLocale from '../../../v2/selectors/isArabicLanguageLocale';
import * as AdyenKlarnaSelectors from '../../../v2/selectors/PaymentOptionSelectors/AdyenKlarna/AdyenKlarnaSelectors';
import {
  getAfterpayClearpayErrorMessage,
  getAfterpayClearpayIsDisabled,
} from '../../../v2/selectors/PaymentOptionSelectors/Afterpay/AfterpaySelectors';
import getAlipayPlusWalletIcons from '../../../v2/selectors/PaymentOptionSelectors/AlipayPlus/getAlipayPlusWalletIcons';
import hasAlipayPlusWalletIcons from '../../../v2/selectors/PaymentOptionSelectors/AlipayPlus/hasAlipayPlusWalletIcons';
import getAmazonPayScriptLoadedStatus
from '../../../v2/selectors/PaymentOptionSelectors/AmazonPay/getAmazonpayScriptLoadStatus';
import getIsAddressInvalidForAmazonpay
from '../../../v2/selectors/PaymentOptionSelectors/AmazonPay/getIsAddressInvalidForAmazonpay';
import getShouldHideAmazonPay from '../../../v2/selectors/PaymentOptionSelectors/AmazonPay/getShouldHideAmazonPay';
import * as ApplePaySelectors from '../../../v2/selectors/PaymentOptionSelectors/ApplePay/ApplePaySelectors';
import getShouldHideFlex from '../../../v2/selectors/PaymentOptionSelectors/Flex/getShouldHideFlex';
import getIsContactNumberInInvalidFormatForFrasersPlus
from '../../../v2/selectors/PaymentOptionSelectors/FrasersPlus/getIsContactNumberInInvalidFormatForFrasersPlus';
import getShouldHideFrasersPlus
from '../../../v2/selectors/PaymentOptionSelectors/FrasersPlus/getShouldHideFrasersPlus';
import {getHasStoredCard} from '../../../v2/selectors/PaymentOptionSelectors/getHasStoredCard';
import getShouldHideCard from '../../../v2/selectors/PaymentOptionSelectors/getShouldHideCard';
import * as GooglePaySelectors from '../../../v2/selectors/PaymentOptionSelectors/GooglePay/GooglePaySelectors';
import getShouldHideKlarna from '../../../v2/selectors/PaymentOptionSelectors/Klarna/getShouldHideKlarna';
import * as KlarnaSelectors from '../../../v2/selectors/PaymentOptionSelectors/Klarna/KlarnaSelectors';
import makeGetDeliverySpecificMessage
from '../../../v2/selectors/PaymentOptionSelectors/makeGetDeliverySpecificMessage';
import {getNewpayErrorMessage} from '../../../v2/selectors/PaymentOptionSelectors/Newpay/getNewpayErrorMessage';
import {getShouldDisableNewpay} from '../../../v2/selectors/PaymentOptionSelectors/Newpay/getShouldDisableNewpay';
import getShouldHidePayOnDelivery
from '../../../v2/selectors/PaymentOptionSelectors/PayOnDelivery/getShouldHidePayOnDelivery';
import getShouldHidePayVyne from '../../../v2/selectors/PaymentOptionSelectors/PayVyne/getShouldHidePayVyne';
import getShouldHideSezzle from '../../../v2/selectors/PaymentOptionSelectors/SezzleSelectors/getShouldHideSezzle';
import getRebrandedTrustlyAttributes
from '../../../v2/selectors/PaymentOptionSelectors/Trustly/getRebrandedTrustlyAttributes';
import getShouldHideTrustly from '../../../v2/selectors/PaymentOptionSelectors/Trustly/getShouldHideTrustly';
import shouldShowRebrandedAdyenTrustly
from '../../../v2/selectors/PaymentOptionSelectors/Trustly/shouldShowRebrandedAdyenTrustly';
import {getWeChatPayShouldHide} from '../../../v2/selectors/PaymentOptionSelectors/WeChatPay/WeChatPaySelectors';
import {
  getCurrency,
  getLocaleFromSiteDataV2,
  getSettings,
} from '../../../v2/selectors/selectors';
import store from '../../../v2/store/store';
import getAdyenV2PaymentOptionAttributes from './getAdyenV2PaymentOptionAttributes';
import getOpttyPaymentOptionAttributes from './getOpttyPaymentOptionAttributes';
import NewCardForm from './NewCard/NewCardForm/NewCardForm';
import getIcon from './paymentOptionSvgs';
import StoredCardLabelledContents from './StoredCard/StoredCardContents/StoredCardLabelledContents';

export const LazyArvatoOptions = lazy(() => import('./Arvato/ArvatoOptions' /* webpackChunkName: 'ArvatoOptions' */));
export const LazyKlarna = lazy(() => import('./Klarna/Klarna' /* webpackChunkName: 'Klarna' */));
export const LazyKlarnaOne = lazy(() => import('./Klarna/KlarnaOne' /* webpackChunkName: 'KlarnaOne' */));
export const LazyLaybuyDetails = lazy(() => import('./Laybuy/LaybuyDetails' /* webpackChunkName: 'LaybuyDetails' */));
export const LazyModeDetails = lazy(() => import('./Mode/ModeDetails' /* webpackChunkName: 'ModeDetails' */));
export const LazyOpenpayDetails = lazy(() => import('./Openpay/OpenpayDetails' /* webpackChunkName: 'OpenpayDetails' */));
export const LazyPayVyneDetails = lazy(() => import('./PayVyne/PayVyneDetails' /* webpackChunkName: 'PayVyneDetails' */));
export const LazySezzleDetails = lazy(() => import('./Sezzle/SezzleDetails' /* webpackChunkName: 'SezzleDetails' */));
export const LazySplititDetails = lazy(() => import('./Splitit/SplititDetails' /* webpackChunkName: 'SplititDetails' */));
export const LazySpotiiDetails = lazy(() => import('./Spotii/SpotiiDetails' /* webpackChunkName: 'SpotiiDetails' */));
export const LazyTrustlyDetails = lazy(() => import('./Trustly/TrustlyDetails' /* webpackChunkName: 'TrustlyDetails' */));
export const LazyZipPayDetails = lazy(() => import('./Zippay/ZippayDetails' /* webpackChunkName: 'ZipPayDetails' */));
export const LazyAtomeDetails = lazy(() => import('./Atome/AtomeDetails' /* webpackChunkName: 'AtomeDetails' */));
export const LazyAmazonPayDetails = lazy(() => import('./AmazonPay/AmazonPayDetails' /* webpackChunkName: 'AmazonPayDetails' */));
export const FlexDetails = lazy(() => import('./Flex/FlexDetails' /* webpackChunkName: 'FlexDetails' */));
export const LazyPayOnDeliveryTitle = lazy(() => import('./PayOnDelivery/PayOnDeliveryTitle' /* webpackChunkName: 'PayOnDeliveryTitle' */));
export const LazyPayOnDeliveryDetails = lazy(() => import('./PayOnDelivery/PayOnDeliveryDetails' /* webpackChunkName: 'PayOnDeliveryDetails' */));
export const LazyStoredCardCV2 = lazy(() => import('./StoredCard/StoredCardCV2/StoredCardCV2' /* webpackChunkName: 'StoredCardCV2' */));
export const LazyKerryForm = lazy(() => import('./KerryForm/KerryForm' /* webpackChunkName: 'KerryForm' */));
export const LazyKCPCreditCard = lazy(() => import('./KCPCreditCard/KCPCreditCardLazyLoader' /* webpackChunkName: 'KCPCreditCardLazyLoader' */));
export const LazyRazorpayTitle = lazy(() => import('./Razorpay/RazorpayTitle' /* webpackChunkName: 'RazorpayTitle' */));
export const RazorpayLazyLoader = lazy(() => import('./Razorpay/RazorpayLazyLoader' /* webpackChunkName: 'RazorpayLazyLoader' */));
export const LazyAfterPayClearPayDetails = lazy(() => import('./AfterpayClearpay/AfterPayClearPayDetails' /* webpackChunkName: 'AfterPayClearPayDetails' */));
export const LazyFrasersPlusDetails = lazy(() => import('./FrasersPlus/FrasersPlusDetails' /* webpackChunkName: 'FrasersPlusDetails' */));
export const LazyAdyenKlarna = lazy(() => import('./AdyenKlarna/AdyenKlarna' /* webpackChunkName: 'AdyenKlarna' */));
export const LazyAdyenPayU = lazy(() => import('./AdyenPayU/AdyenPayU' /* webpackChunkName: 'AdyenPayU' */));
export const LazyPaypalInThreeDetails = lazy(() => import('./PaypalInThree/PaypalInThreeDetails' /* webpackChunkName: 'PaypalInThreeDetails' */));
export const LazyAlipayPlusDetails = lazy(() => import('./AlipayPlus/AlipayPlusDetails' /* webpackChunkName: 'AlipayPlusDetails' */));
export const LazyNewpayDetails = lazy(() => import('./Newpay/NewpayDetails' /* webpackChunkName: 'NewpayDetails' */));

const storedCardToIcon = (cardName) => {
  const cardToIconObj = {
    'american express': 'amex',
    visa: 'visa-credit',
    'visa electron': 'visa-debit',
  };
  
  const cardNameLower = cardName.toLowerCase();
  return getIcon(cardToIconObj[cardNameLower] || cardNameLower);
  // If no corresponding icon name is set, just use the cardName itself.
};

export const paymentOptionAttributes = (paymentOption) => {
  const state = store.getState();
  
  const showRebrandedAdyenTrustly = shouldShowRebrandedAdyenTrustly(state);
  const trustlyAttributes = getRebrandedTrustlyAttributes(state);
  
  const cardDefaults = { // Used for 'new credit card' and 'adyen card'
    title: 'checkout.payment.method.field.label.pay.with.credit.card.text',
    iconTitle: 'checkout.payment.method.field.label.pay.with.credit.card.icon.title',
    shortName: 'card',
    paymentName: 'checkout.payment.method.field.label.pay.with.credit.card.text',
    alwaysShowTitle: true, // Irrespective of the site's config
    icons: getDisplayableCards(state),
    paymentOptionContents: NewCardForm,
    getShouldHide: () => getShouldHideCard(state),
    getIsDisabled: () => getShouldHideCard(state),
  };
  
  if (paymentOption.isAdyenV2Option) {
    // eslint-disable-next-line no-underscore-dangle
    return getAdyenV2PaymentOptionAttributes(paymentOption.type, paymentOption.component?._id || paymentOption.type, state);
  }
  
  if (paymentOption.isOpttyOption) {
    return getOpttyPaymentOptionAttributes(paymentOption, state);
  }
  
  switch (paymentOption.type) {
    case 'ADYEN_V2':
      return {
        getShouldHide: () => true,
      };
  
    case 'OPTTY':
      return {
        getShouldHide: () => true,
      };
  
    case 'GIFT_CARD':
      return {
        getShouldHide: () => true,
      };
    
    case 'DIGGECARD':
      return {
        getShouldHide: () => true,
      };
    
    case 'NEW_CREDIT_CARD':
      if (getHasStoredCard(state)) { // Has stored card, so display different details
        return {
          ...cardDefaults,
          shortName: 'new-card',
          title: 'checkout.payment.method.link.add.new.card.text',
          paymentName: 'checkout.payment.method.link.add.new.card.text',
        };
      }
      return cardDefaults;
    
    case 'ADYEN-CARD':
      return {...cardDefaults, paymentOptionContents: null};
    
    case 'STORED_CREDIT_CARD': {
      const cardCount = Number(paymentOption.displayId) - 1000;
      return {
        inputId: `pay-with-card-${cardCount}`,
        icons: storedCardToIcon(paymentOption.cardType),
        paymentOptionContents: LazyStoredCardCV2,
        paymentOptionLabelledContents: StoredCardLabelledContents,
        getShouldHide: () => getShouldHideCard(state),
        getIsDisabled: () => getShouldHideCard(state),
      };
    }
  
    case 'PAYPAL':
      return {
        title: 'checkout.payment.method.paypal.name',
        getMessage: () => {
          if (paymentOption.surchargePercentage) {
            return {
              type: MessageType.INFO,
              translationKey: 'checkout.payment.method.field.help.paypal.surcharge.message.text',
              embeddedValues: [`${paymentOption.surchargePercentage}%`],
            };
          }
          return null;
        },
      };
  
    case 'AMAZONPAY':
      return {
        paymentOptionContents: LazyAmazonPayDetails,
        getShouldHide: getShouldHideAmazonPay,
        getIsDisabled: () => !!(getAmazonPayScriptLoadedStatus(store.getState()) === 'SCRIPT_FAILED_TO_LOAD' || getIsAddressInvalidForAmazonpay(store.getState())),
        getMessage: () => {
          if (getAmazonPayScriptLoadedStatus(store.getState()) === 'SCRIPT_FAILED_TO_LOAD') {
            return {
              type: MessageType.ERROR,
              translationKey: 'amazonpay.script.load.error',
              embeddedValues: [],
            };
          }
          
          if (getIsAddressInvalidForAmazonpay(store.getState())) {
            return {
              type: MessageType.ERROR,
              translationKey: 'amazonpay.address.error',
              embeddedValues: [],
            };
          }
          return null;
        },
      };
  
    case 'PAY_ON_DELIVERY':
      return {
        customTitleIcon: LazyPayOnDeliveryTitle,
        getShouldHide: getShouldHidePayOnDelivery,
        paymentOptionContents: LazyPayOnDeliveryDetails,
      };
    
    case 'PAYPAL_IN_3':
      return {
        title: 'checkout.payment.method.paypal.name',
        getMessage: () => {
          if (paymentOption.surchargePercentage) {
            return {
              type: MessageType.INFO,
              translationKey: 'checkout.payment.method.field.help.paypal.surcharge.message.text',
              embeddedValues: [`${paymentOption.surchargePercentage}%`],
            };
          }
          return null;
        },
        paymentOptionContents: LazyPaypalInThreeDetails,
      };
    
    case 'ADYEN-SEPADIRECTDEBIT':
      return {
        iconTitle: 'checkout.payment.method.sepadirectdebit.field.label.pay.with.icon.alt',
        getMessage: () => ({
          type: MessageType.INFO,
          translationKey: 'checkout.payment.sepa.delay.message',
        }),
      };
    
    case 'ADYEN-KCP_BANKTRANSFER': // intentional fallthrough
    case 'KCP_BANK':
      return {
        iconTitle: 'checkout.payment.method.kcp_banktransfer.field.label.pay.with.icon.alt',
        getMessage: () => ({
          type: MessageType.INFO,
          translationKey: 'checkout.payment.method.kcp_banktransfer.information.text',
        }),
      };
    
    case 'ADYEN-KCP_CREDITCARD': // intentional fallthrough
    case 'KCP_CARD':
      return {
        iconTitle: 'checkout.payment.method.kcp_creditcard.field.label.pay.with.icon.alt',
        getMessage: () => ({
          type: MessageType.INFO,
          translationKey: 'checkout.payment.method.kcp_creditcard.information.text',
        }),
        paymentOptionContents: LazyKCPCreditCard,
      };
    case 'RAZORPAY':
      return {
        customTitleIcon: LazyRazorpayTitle,
        paymentOptionContents: RazorpayLazyLoader,
      };
    case 'ADYEN-MOLPAY_EBANKING_MY':
      return {
        shortName: 'molpay_ebanking_MY',
        iconTitle: 'checkout.payment.method.molpay_ebanking_MY.field.label.pay.with.icon.alt',
        getMessage: () => ({
          type: MessageType.INFO,
          translationKey: 'checkout.payment.method.molpay_ebanking_MY.information.text',
        }),
      };
    
    case 'ADYEN-MOLPAY_EBANKING_FPX_MY':
      return {
        shortName: 'molpay_ebanking_fpx_MY',
        iconTitle: 'checkout.payment.method.molpay_ebanking_fpx_MY.field.label.pay.with.icon.alt',
        getMessage: () => ({
          type: MessageType.INFO,
          translationKey: 'checkout.payment.method.molpay_ebanking_fpx_MY.information.text',
        }),
      };
  
    case 'ADYEN-DIRECTEBANKING': // These 3 have strange capitalisation...
      return {shortName: 'directEbanking'};
  
    case 'ADYEN-EBANKING_FI':
      return {
        shortName: 'ebanking_FI',
        paymentName: 'checkout.payment.method.ebanking_FI.name',
        icons: [
          getIcon('ebankingFIPaytrail'),
          getIcon('ebankingFIAkita'),
          getIcon('ebankingFIAlandsbanken'),
          getIcon('ebankingFIDanskeBank'),
          getIcon('ebankingFIHandelsbanken'),
          getIcon('ebankingFINordea'),
          getIcon('ebankingFIOpBank'),
          getIcon('ebankingFIPopBankFinland'),
          getIcon('ebankingFIPopBankSweden'),
          getIcon('ebankingFISaastopankki')],
        useFullSizeIconsInArray: true,
      };
    
    case 'ADYEN-ECONTEXT_STORES':
      return {
        paymentName: 'checkout.payment.method.econtext.stores.name',
        icons: [getIcon('econtextStoresCircleK'),
          getIcon('econtextStoresFamilyMart'),
          getIcon('econtextStoresLawson'),
          getIcon('econtextStoresMiniStop'),
          getIcon('econtextStoresSeicomart')],
        useFullSizeIconsInArray: true,
      };
  
    case 'ADYEN-PAYU_IN':
      return {
        shortName: 'payu_IN',
        paymentOptionContents: LazyAdyenPayU,
        getShouldHide: () => !!getSettings(state)?.should_hide_and_disable_adyen_v1_payu,
        getIsDisabled: () => !!getSettings(state)?.should_hide_and_disable_adyen_v1_payu,
      };
  
    case 'ADYEN-PAYTM':
      return {
        shortName: 'paytm',
        iconTitle: 'checkout.payment.method.paytm.name',
      };
    
    case 'ADYEN-TRUSTLY':
      return {
        icons: getIcon(showRebrandedAdyenTrustly ? 'trustly' : 'trustly-old'),
        helpText: showRebrandedAdyenTrustly && trustlyAttributes.helpText
          ? 'checkout.payment.method.help.text.trustly'
          : null,
        paymentOptionContents: showRebrandedAdyenTrustly ? LazyTrustlyDetails : null,
      };
    
    case 'TRUSTLY':
      return {
        helpText: trustlyAttributes.helpText,
        paymentOptionContents: LazyTrustlyDetails,
        getShouldHide: getShouldHideTrustly,
      };
  
    case 'ALIPAY_PLUS': {
      const alipayPlusDefaultIcon = getCurrency(state)?.toLowerCase() === 'aud'
        ? getIcon('alipay_plus_au_currency') : getIcon('alipay_plus');
  
      return {
        getMessage: getSettings(state).use_kerry_form
          ? () => null
          : makeGetDeliverySpecificMessage(paymentOption.type),
        paymentOptionContents: LazyAlipayPlusDetails,
        icons: {
          ...!hasAlipayPlusWalletIcons(state) ? alipayPlusDefaultIcon : getAlipayPlusWalletIcons(state),
          collapsedIconUrl: getIcon('alipay_plus_collapsed').url,
          pillowUrl: getIcon('alipay_plus')?.pillowUrl,
        },
        useInitialHeight: true,
      };
    }
  
    case 'KLARNA':
      return {
        icons: getIcon('klarna_payment'),
        title: KlarnaSelectors.getKlarnaOneTitle,
        iconTitle: 'checkout.payment.method.klarna.name',
        paymentOptionContents: LazyKlarnaOne,
        getIsDisabled: KlarnaSelectors.getKlarnaOneIsDisabled,
        getMessage: KlarnaSelectors.getKlarnaOneErrorMessage,
      };
    
    case 'KLARNA_LATER':
      return {
        icons: getIcon('klarna_payment'),
        title: KlarnaSelectors.getKlarnaLaterTitle,
        alwaysShowTitle: true, // Irrespective of the site's config
        iconTitle: 'payments.klarna.pay.later',
        paymentOptionContents: LazyKlarna,
        getIsDisabled: KlarnaSelectors.getKlarnaLaterIsDisabled,
        getMessage: KlarnaSelectors.getKlarnaLaterErrorMessage,
      };
    
    case 'KLARNA_NOW':
      return {
        icons: getIcon('klarna_payment'),
        title: 'payments.klarna.pay.now',
        alwaysShowTitle: true, // Irrespective of the site's config
        iconTitle: 'payments.klarna.pay.now',
        paymentOptionContents: LazyKlarna,
        getIsDisabled: KlarnaSelectors.getKlarnaNowIsDisabled,
        getMessage: KlarnaSelectors.getKlarnaNowErrorMessage,
      };
    
    case 'KLARNA_SLICE':
      return {
        icons: getIcon('klarna_payment'),
        title: KlarnaSelectors.getKlarnaSliceTitle,
        alwaysShowTitle: !getSettings(state)?.should_not_output_klarna_slice_title, // Irrespective of the site's config
        iconTitle: 'payments.klarna.pay.over.time',
        paymentOptionContents: LazyKlarna,
        getIsDisabled: KlarnaSelectors.getKlarnaSliceIsDisabled,
        getMessage: KlarnaSelectors.getKlarnaSliceErrorMessage,
        getShouldHide: getShouldHideKlarna,
      };
    
    case 'AFTERPAY':
    case 'CLEARPAY':
      return {
        icons: getIcon((getLocaleFromSiteDataV2(state) === 'en_US')
          ? 'afterpayUS'
          : (getLocaleFromSiteDataV2(state) === 'en_GB') ? 'clearpay' : 'afterpay'),
        getIsDisabled: getAfterpayClearpayIsDisabled,
        getMessage: getAfterpayClearpayErrorMessage,
        getShouldHide: getAfterpayClearpayIsDisabled,
        paymentOptionContents: LazyAfterPayClearPayDetails,
      };
    
    // Adyen Klarna
    case 'ADYEN-KLARNA':
      return {
        getIsDisabled: AdyenKlarnaSelectors.getAdyenKlarnaIsDisabled,
        getMessage: AdyenKlarnaSelectors.getAdyenKlarnaErrorMessage,
        paymentOptionContents: LazyAdyenKlarna,
      };
    
    case 'GOOGLEPAY':
      return {
        getShouldHide: GooglePaySelectors.getShouldHideGooglePay,
      };
    
    case 'APPLEPAY':
      return {
        getMessage: ApplePaySelectors.getApplePayMessage,
        getShouldHide: ApplePaySelectors.getShouldHideApplePay,
      };
    
    case 'WECHATPAY':
      return {
        getShouldHide: getWeChatPayShouldHide,
        getIsDisabled: getWeChatPayShouldHide,
        getMessage: getSettings(state).use_kerry_form
          ? () => null
          : makeGetDeliverySpecificMessage(paymentOption.type),
        paymentOptionContents: LazyKerryForm,
      };
    
    case 'UNIONPAY':
      return {
        shortName: 'UnionPay',
        icons: getIcon('unionpay-new'),
      };
    
    case 'LAYBUY':
      return {
        paymentOptionContents: LazyLaybuyDetails,
      };
    
    case 'SPLITIT':
      return {
        paymentOptionContents: LazySplititDetails,
      };
    
    case 'OPENPAY':
      return {
        paymentOptionContents: LazyOpenpayDetails,
      };
    
    case 'ARVATO':
      return {
        paymentOptionContents: LazyArvatoOptions,
      };
    
    case 'ZIPPAY':
    case 'QUADPAY':
      return {
        paymentOptionContents: LazyZipPayDetails,
      };
    
    case 'SPOTII':
      return {
        paymentOptionContents: LazySpotiiDetails,
        icons: getIcon(isArabicLanguageLocale(state) ? 'spotiiAR' : 'spotii'),
      };
    
    case 'SEZZLE':
      return {
        getShouldHide: getShouldHideSezzle,
        paymentOptionContents: LazySezzleDetails,
      };
    
    case 'MODE':
      return {
        paymentOptionContents: LazyModeDetails,
      };
    
    case 'PAYVYNE':
      return {
        paymentOptionContents: LazyPayVyneDetails,
        getShouldHide: getShouldHidePayVyne,
      };
    
    case 'ATOME':
      return {
        paymentOptionContents: LazyAtomeDetails,
      };

    case 'NEWPAY':
      return {
        paymentOptionContents: LazyNewpayDetails,
        getIsDisabled: getShouldDisableNewpay,
        getMessage: getNewpayErrorMessage,
      };
      
    case 'FRASERS_PLUS':
      return {
        paymentOptionContents: LazyFrasersPlusDetails,
        getShouldHide: getShouldHideFrasersPlus,
        getIsDisabled: () => !!getIsContactNumberInInvalidFormatForFrasersPlus(store.getState(), true),
        getMessage: () => {
          if (getIsContactNumberInInvalidFormatForFrasersPlus(store.getState())) {
            return {
              type: MessageType.ERROR,
              translationKey: 'frasersplus.contact.number.format',
              embeddedValues: [],
            };
          }
          return null;
        },
      };
      
    case 'FLEX':
      return {
        paymentOptionContents: FlexDetails,
        getShouldHide: getShouldHideFlex,
      }

    default:
      return {};
  }
};
